import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { errorMessage } from "@/lib/errors";
import { trackEvent } from "@/lib/analytics";
import {
  Loader2, MapPin, Lock, Sparkles, Send, Coins, Crown, ArrowLeft, Heart, Wand2, Flag, Ban, Gift, X as XIcon,
} from "lucide-react";
import VerifiedBadge from "@/components/VerifiedBadge";
import NumericRating from "@/components/NumericRating";
import RatingConfetti from "@/components/RatingConfetti";
import { VoiceIntroPlayer } from "@/components/VoiceIntroRecorder";
import { useAuth } from "@/lib/auth";

/**
 * Public profile page — /u/:userId.
 * Shows full identity unless the target is Golden AND the viewer hasn't unlocked
 * them, in which case the top photo is blurred and reveal CTAs are surfaced.
 */
export default function ProfileView() {
  const { userId } = useParams();
  const nav = useNavigate();
  const { user: viewer, refresh } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [activeIdx, setActiveIdx] = useState(0);
  const [confettiKey, setConfettiKey] = useState(0);
  const [ratingBusyId, setRatingBusyId] = useState(null);
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [icebreakers, setIcebreakers] = useState([]);
  const [icebreakersLoading, setIcebreakersLoading] = useState(false);
  const [giftOpen, setGiftOpen] = useState(false);
  const [giftNote, setGiftNote] = useState("");
  const [giftSending, setGiftSending] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/users/${userId}`);
      setData(data);
      setActiveIdx(0);
    } catch (err) {
      toast.error(errorMessage(err, "Couldn't load profile"));
      nav("/discover");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [userId]);

  // Self view is allowed — members can preview their own public profile and copy
  // a shareable link. A small banner in the CTA area lets them jump to /profile
  // to edit if they need to tweak something.
  const isSelf = data?.self === true;

  const ratePhoto = async (photoId, rating) => {
    if (ratingBusyId) return;
    setRatingBusyId(photoId);
    try {
      const { data: r } = await api.post("/rate", { photo_id: photoId, rating });
      trackEvent("rate_photo", { score: rating, is_high: rating >= 8, source: "profile" });
      // Merge the fresh owner payload into local state so the strip reflects the new my_rating + avg.
      const owner = r.owner;
      if (owner) {
        setData((d) => d && ({
          ...d,
          user: {
            ...d.user,
            photos: d.user.photos.map((p) => {
              const fresh = (owner.photos || []).find((x) => x.id === p.id);
              return fresh ? { ...p, ...fresh } : p;
            }),
            overall_rating: owner.overall_rating ?? d.user.overall_rating,
          },
        }));
      }
      if (rating >= 9) setConfettiKey((k) => k + 1);
      if (r.just_golden) toast(`✨ You pushed this photo to Golden (8+)!`, { duration: 4200 });
      if (r.milestone) toast.success(`🏆 ${r.milestone} lifetime ratings!`, { duration: 4200 });
      if (r.streak?.credit_awarded) toast.success(`🔥 ${r.streak.streak}-day streak! +1 reveal credit`, { duration: 4200 });
      else toast.success("Rating saved");
    } catch (err) {
      toast.error(errorMessage(err, "Could not save rating"));
    } finally {
      setRatingBusyId(null);
    }
  };

  const spendCredit = async () => {
    setBusy(true);
    try {
      const { data: r } = await api.post(`/profiles/${userId}/spend-credit`);
      trackEvent("reveal_credit_spent", { target_id: userId, method: "credit" });
      toast.success("Revealed — say hello!");
      if (refresh) await refresh();
      nav(`/chat/${r.match_id}`);
    } catch (err) {
      toast.error(errorMessage(err, "Couldn't reveal"));
    } finally { setBusy(false); }
  };

  const payReveal = async () => {
    setBusy(true);
    try {
      const { data: r } = await api.post(`/profiles/${userId}/reveal-checkout`, {
        origin_url: window.location.origin,
      });
      trackEvent("begin_checkout", {
        currency: "USD",
        value: 10,
        items: [{ item_id: userId, item_name: "reveal", price: 10 }],
      });
      if (r.checkout_url) window.location.href = r.checkout_url;
    } catch (err) {
      toast.error(errorMessage(err, "Checkout failed"));
    } finally { setBusy(false); }
  };

  const sendMessage = async () => {
    const text = message.trim();
    if (!text || !data?.match_id) return;
    setSending(true);
    try {
      await api.post(`/matches/${data.match_id}/messages`, { text });
      trackEvent("send_message", { source: "profile", chars: text.length });
      setMessage("");
      setSent(true);
      toast.success("Message sent");
    } catch (err) {
      toast.error(errorMessage(err, "Couldn't send message"));
    } finally {
      setSending(false);
    }
  };

  const suggestOpeners = async () => {
    if (!data?.match_id || icebreakersLoading) return;
    setIcebreakersLoading(true);
    try {
      const { data: r } = await api.post(`/matches/${data.match_id}/icebreakers`);
      setIcebreakers(r.openers || []);
      if (!r.openers || r.openers.length === 0) toast.error("No openers came back — try again");
    } catch (err) {
      toast.error(errorMessage(err, "Couldn't fetch openers"));
    } finally {
      setIcebreakersLoading(false);
    }
  };

  const blockMember = async () => {
    if (!window.confirm(`Block ${data.user.display_name || "this member"}? You won't see them and they won't see you.`)) return;
    try {
      await api.post("/block", { target_id: userId });
      toast.success("Member blocked");
      nav("/discover");
    } catch (err) { toast.error(errorMessage(err, "Couldn't block")); }
  };

  const sendGift = async () => {
    if (giftSending) return;
    setGiftSending(true);
    try {
      const { data: r } = await api.post("/gifts/send", {
        recipient_id: userId,
        note: giftNote.trim() || undefined,
      });
      trackEvent("gift_sent", { was_free: r.was_free, has_note: giftNote.trim().length > 0 });
      toast.success(
        r.was_free
          ? "Gift sent — free with Prime"
          : `Gift sent — ${r.sender_credits_left} credit${r.sender_credits_left === 1 ? "" : "s"} left`
      );
      setGiftOpen(false);
      setGiftNote("");
      if (refresh) await refresh();
    } catch (err) {
      toast.error(errorMessage(err, "Couldn't send gift"));
    } finally {
      setGiftSending(false);
    }
  };

  const reportMember = async () => {
    const reasonOpts = "inappropriate_photos, harassment, fake_profile, spam, safety, other";
    const reason = window.prompt(`Report reason (choose one):\n${reasonOpts}`, "harassment");
    if (!reason) return;
    const notes = window.prompt("Optional — share more context (max 1000 chars):", "") || "";
    try {
      await api.post("/report", { target_id: userId, reason: reason.trim(), notes });
      toast.success("Report filed — the member has been auto-blocked. Thank you.");
      nav("/discover");
    } catch (err) { toast.error(errorMessage(err, "Couldn't file report")); }
  };

  if (loading || !data) {
    return (
      <div className="min-h-[70vh] flex items-center justify-center" data-testid="profile-view-loading">
        <Loader2 className="w-8 h-8 animate-spin text-[#722f37]" />
      </div>
    );
  }

  const u = data.user;
  const locked = data.locked;
  const isGolden = data.is_golden;
  const credits = viewer?.reveal_credits || 0;
  const subscribed = data.subscribed;
  const photos = u.photos || [];

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 py-6 md:py-10" data-testid="profile-view-root">
      <button
        onClick={() => nav(-1)}
        className="flex items-center gap-1.5 text-sm text-[#722f37] hover:underline mb-4"
        data-testid="profile-view-back"
      >
        <ArrowLeft className="w-4 h-4" /> Back
      </button>

      <div className="grid grid-cols-1 md:grid-cols-[minmax(0,3fr)_minmax(0,2fr)] gap-6 md:gap-8">
        {/* Photo gallery */}
        <div className="min-w-0">
          <div
            className="relative aspect-[3/4] rounded-[1.5rem] overflow-hidden bg-[#eae5d9] card-elev"
            data-testid="profile-view-hero"
          >
            {photos[activeIdx] || photos[0] ? (
              <img
                src={(photos[activeIdx] || photos[0]).data_url}
                alt=""
                className={`w-full h-full object-cover ${locked ? "blur-2xl scale-110" : ""}`}
                data-testid="profile-view-hero-photo"
              />
            ) : (
              <div className="w-full h-full" style={{ background: "linear-gradient(135deg,#c9b8a0,#a08a70)" }} />
            )}

            {isGolden && (
              <div className="absolute top-4 left-4 rounded-full bg-[#c39d2b] text-[#2a2010] px-3 py-1.5 text-xs font-bold flex items-center gap-1 shadow" data-testid="profile-view-golden-badge">
                <Crown className="w-3.5 h-3.5" /> GOLDEN
              </div>
            )}

            {typeof u.overall_rating === "number" && u.rating_count > 0 && (
              <div className="absolute top-4 right-4 glass rounded-full px-3 py-1.5 text-xs font-semibold text-[#722f37] flex items-center gap-1" data-testid="profile-view-rating">
                <Sparkles className="w-3.5 h-3.5 text-[#c39d2b]" />
                {u.overall_rating.toFixed(1)}/10 · {u.rating_count}
              </div>
            )}

            {locked && (
              <div className="absolute inset-0 flex flex-col items-center justify-center bg-black/30 text-white text-center p-6">
                <Lock className="w-8 h-8 mb-2" />
                <p className="text-lg font-serif-display">Hidden until revealed</p>
                <p className="text-xs opacity-80 mt-1">This member is Golden — a favourite of the community.</p>
              </div>
            )}

            <RatingConfetti trigger={confettiKey} />
          </div>

          {/* Thumbnail rail (only when unlocked) */}
          {!locked && photos.length > 1 && (
            <div className="mt-3 grid grid-cols-4 gap-2" data-testid="profile-view-thumbs">
              {photos.map((p, i) => (
                <button
                  key={p.id}
                  type="button"
                  onClick={() => setActiveIdx(i)}
                  className={`aspect-square rounded-lg overflow-hidden bg-[#eae5d9] transition-all ${
                    i === activeIdx ? "ring-2 ring-[#722f37] ring-offset-1" : "opacity-80 hover:opacity-100"
                  }`}
                  data-testid={`profile-view-thumb-${i}`}
                  aria-label={`Show photo ${i + 1}`}
                >
                  <img src={p.data_url} alt="" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          )}

          {/* RATE-IN-PROFILE — 1-10 strip for the currently-shown photo */}
          {!locked && photos[activeIdx] && (
            <div
              className="mt-4 p-5 rounded-2xl bg-white border border-[#e5e0d8] card-elev"
              data-testid="profile-view-rate-strip"
            >
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <div>
                  <div className="text-[11px] uppercase tracking-[0.28em] font-bold text-[#722f37]">Rate this photo</div>
                  <div className="text-xs text-[#5c5c5c] mt-0.5">
                    {photos[activeIdx].rating_count
                      ? `Averaging ${photos[activeIdx].avg_rating}/10 across ${photos[activeIdx].rating_count} rating${photos[activeIdx].rating_count === 1 ? "" : "s"}`
                      : "Be the first to rate."}
                    {photos[activeIdx].my_rating != null && (
                      <span className="ml-1 text-[#c39d2b] font-semibold">
                        · You rated {photos[activeIdx].my_rating}/10
                      </span>
                    )}
                  </div>
                </div>
                {ratingBusyId === photos[activeIdx].id && (
                  <Loader2 className="w-4 h-4 animate-spin text-[#722f37]" />
                )}
              </div>
              <div className="mt-3">
                <NumericRating
                  value={photos[activeIdx].my_rating || 0}
                  onChange={(n) => ratePhoto(photos[activeIdx].id, n)}
                  testId={`profile-view-rate-${photos[activeIdx].id}`}
                  readOnly={!!ratingBusyId}
                />
              </div>
            </div>
          )}
        </div>

        {/* Identity + actions */}
        <div className="min-w-0 flex flex-col gap-5">
          <div>
            <h1 className="font-serif-display text-3xl sm:text-4xl leading-tight text-[#1a1a1a] break-words" data-testid="profile-view-name">
              {locked ? "Hidden until revealed" : `${u.display_name}${u.age ? `, ${u.age}` : ""}`}
              {!locked && u.verified && <span className="inline-block ml-2 align-middle"><VerifiedBadge size={14} /></span>}
            </h1>
            {u.location && (
              <div className="mt-1.5 flex items-center gap-1 text-sm text-[#5c5c5c]" data-testid="profile-view-location">
                <MapPin className="w-4 h-4" /> {u.location}
              </div>
            )}
            {typeof data.compatibility === "number" && (
              <div className="mt-3 inline-flex items-center gap-2 rounded-full bg-[#722f37] text-white px-3 py-1 text-xs font-semibold" data-testid="profile-view-compat">
                <Heart className="w-3.5 h-3.5" /> {data.compatibility}% match
              </div>
            )}
          </div>

          {u.voice_intro_url && (
            <div data-testid="profile-view-voice">
              <VoiceIntroPlayer url={u.voice_intro_url} durationMs={u.voice_intro_duration_ms} testId="profile-view-voice-play" />
            </div>
          )}

          {!locked && u.bio && (
            <p className="text-sm text-[#1a1a1a] leading-relaxed whitespace-pre-line" data-testid="profile-view-bio">
              {u.bio}
            </p>
          )}

          {!locked && u.interests?.length > 0 && (
            <div className="flex flex-wrap gap-1.5" data-testid="profile-view-interests">
              {u.interests.map((tag) => (
                <span key={tag} className="px-2.5 py-1 rounded-full bg-[#faf9f6] border border-[#e5e0d8] text-xs text-[#5c5c5c]">
                  {tag}
                </span>
              ))}
            </div>
          )}

          {/* CTA row */}
          <div className="mt-2 flex flex-col gap-2">
            {isSelf ? (
              <div className="rounded-2xl bg-white border border-[#e5e0d8] card-elev p-4" data-testid="profile-view-self-panel">
                <div className="text-[11px] uppercase tracking-[0.28em] font-bold text-[#722f37]">
                  Preview · this is how others see you
                </div>
                <div className="mt-3 flex items-center gap-2 rounded-xl bg-[#faf9f6] border border-[#e5e0d8] p-2.5">
                  <code className="flex-1 truncate text-xs text-[#1a1a1a]" data-testid="profile-view-self-link">
                    {`${window.location.origin}/u/${u.id}`}
                  </code>
                  <button
                    type="button"
                    onClick={() => {
                      navigator.clipboard.writeText(`${window.location.origin}/u/${u.id}`);
                      toast.success("Profile link copied");
                    }}
                    className="rounded-full bg-[#722f37] text-white text-xs font-semibold px-3 py-1.5 hover:bg-[#8c3a45]"
                    data-testid="profile-view-copy-link"
                  >
                    Copy link
                  </button>
                </div>
                <Link
                  to="/profile"
                  className="mt-3 btn-primary w-full flex items-center justify-center gap-2 !py-2.5"
                  data-testid="profile-view-edit"
                >
                  Edit your profile
                </Link>
              </div>
            ) : locked ? (
              <>
                {subscribed ? (
                  <button
                    onClick={spendCredit}
                    disabled={busy}
                    className="btn-primary w-full flex items-center justify-center gap-2 !py-3"
                    data-testid="profile-view-contact-sub"
                  >
                    {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                    Contact (included)
                  </button>
                ) : credits > 0 ? (
                  <>
                    <button
                      onClick={spendCredit}
                      disabled={busy}
                      className="btn-primary w-full flex items-center justify-center gap-2 !py-3"
                      data-testid="profile-view-spend-credit"
                    >
                      {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Coins className="w-4 h-4" />}
                      Reveal with 1 credit ({credits} left)
                    </button>
                    <button
                      onClick={payReveal}
                      disabled={busy}
                      className="text-xs text-[#5c5c5c] underline hover:text-[#722f37] text-center"
                      data-testid="profile-view-pay-reveal-alt"
                    >
                      or pay $10
                    </button>
                  </>
                ) : (
                  <>
                    <button
                      onClick={payReveal}
                      disabled={busy}
                      className="btn-primary w-full flex items-center justify-center gap-2 !py-3"
                      data-testid="profile-view-pay-reveal"
                    >
                      {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Lock className="w-4 h-4" />}
                      Reveal for $10
                    </button>
                    <Link
                      to="/pricing"
                      className="text-xs text-[#5c5c5c] underline hover:text-[#722f37] text-center"
                      data-testid="profile-view-see-plans"
                    >
                      or unlock everyone with a membership
                    </Link>
                  </>
                )}
              </>
            ) : data.match_id ? (
                <div className="rounded-2xl bg-white border border-[#e5e0d8] card-elev p-4" data-testid="profile-view-composer">
                  <div className="flex items-center justify-between gap-2">
                    <label htmlFor="pv-msg" className="text-[11px] uppercase tracking-[0.28em] font-bold text-[#722f37]">
                      Say hello
                    </label>
                    <button
                      type="button"
                      onClick={suggestOpeners}
                      disabled={icebreakersLoading}
                      className="inline-flex items-center gap-1 text-[11px] font-semibold text-[#722f37] hover:text-[#8c3a45] disabled:opacity-50"
                      data-testid="profile-view-suggest-openers"
                    >
                      {icebreakersLoading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Wand2 className="w-3 h-3" />}
                      Suggest openers
                    </button>
                  </div>
                  {icebreakers.length > 0 && (
                    <div className="mt-3 flex flex-col gap-1.5" data-testid="profile-view-icebreakers">
                      {icebreakers.map((line, i) => (
                        <button
                          key={i}
                          type="button"
                          onClick={() => { setMessage(line); setSent(false); }}
                          className="text-left text-xs rounded-lg border border-[#c39d2b]/40 bg-[#c39d2b]/5 hover:bg-[#c39d2b]/10 px-3 py-2 text-[#1a1a1a] transition-colors"
                          data-testid={`profile-view-icebreaker-${i}`}
                        >
                          <Sparkles className="inline w-3 h-3 mr-1 text-[#c39d2b]" />
                          {line}
                        </button>
                      ))}
                    </div>
                  )}
                  <textarea
                    id="pv-msg"
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    placeholder={`A warm opener for ${u.display_name}...`}
                    rows={3}
                    maxLength={2000}
                    className="mt-3 w-full rounded-xl border border-[#e5e0d8] bg-[#faf9f6] px-3 py-2 text-sm text-[#1a1a1a] resize-none focus:outline-none focus:border-[#722f37] focus:bg-white"
                    data-testid="profile-view-composer-input"
                  />
                  <div className="mt-2 flex items-center justify-between gap-2">
                    <span className="text-[11px] text-[#5c5c5c]" data-testid="profile-view-composer-count">
                      {message.length}/2000
                    </span>
                    <div className="flex items-center gap-2">
                      {sent && (
                        <Link
                          to={`/chat/${data.match_id}`}
                          className="text-xs text-[#722f37] underline"
                          data-testid="profile-view-open-chat"
                        >
                          Open chat →
                        </Link>
                      )}
                      <button
                        type="button"
                        onClick={sendMessage}
                        disabled={sending || !message.trim()}
                        className="btn-primary flex items-center gap-1.5 !py-2 !px-4 text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                        data-testid="profile-view-send-message"
                      >
                        {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                        Send
                      </button>
                    </div>
                  </div>
                </div>
            ) : (
              // Profile is visible (non-Golden) but the viewer still needs to unlock to message.
              credits > 0 ? (
                <button
                  onClick={spendCredit}
                  disabled={busy}
                  className="btn-primary w-full flex items-center justify-center gap-2 !py-3"
                  data-testid="profile-view-spend-to-message"
                >
                  {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Coins className="w-4 h-4" />}
                  Unlock to message ({credits} credit{credits === 1 ? "" : "s"})
                </button>
              ) : (
                <>
                  <button
                    onClick={payReveal}
                    disabled={busy}
                    className="btn-primary w-full flex items-center justify-center gap-2 !py-3"
                    data-testid="profile-view-pay-to-message"
                  >
                    {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                    Unlock to message · $10
                  </button>
                  <Link
                    to="/pricing"
                    className="text-xs text-[#5c5c5c] underline hover:text-[#722f37] text-center"
                    data-testid="profile-view-see-plans-msg"
                  >
                    or unlock everyone with a membership
                  </Link>
                </>
              )
            )}
          </div>

          {!isSelf && (
            <div className="mt-4 flex items-center gap-3 text-xs text-[#5c5c5c]" data-testid="profile-view-safety">
              <button
                type="button"
                onClick={() => setGiftOpen(true)}
                className="inline-flex items-center gap-1 text-[#722f37] font-semibold hover:text-[#8c3a45]"
                data-testid="profile-view-gift"
              >
                <Gift className="w-3.5 h-3.5" /> Gift a reveal
              </button>
              <span className="opacity-50">·</span>
              <button
                type="button"
                onClick={reportMember}
                className="inline-flex items-center gap-1 hover:text-[#a02b2b]"
                data-testid="profile-view-report"
              >
                <Flag className="w-3 h-3" /> Report
              </button>
              <span className="opacity-50">·</span>
              <button
                type="button"
                onClick={blockMember}
                className="inline-flex items-center gap-1 hover:text-[#a02b2b]"
                data-testid="profile-view-block"
              >
                <Ban className="w-3 h-3" /> Block
              </button>
            </div>
          )}
        </div>
      </div>

      {/* GIFT A REVEAL — modal */}
      {giftOpen && !isSelf && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4"
          onClick={() => !giftSending && setGiftOpen(false)}
          data-testid="gift-modal-backdrop"
        >
          <div
            className="w-full max-w-md rounded-2xl bg-white border border-[#e5e0d8] card-elev p-6 relative"
            onClick={(e) => e.stopPropagation()}
            data-testid="gift-modal"
          >
            <button
              type="button"
              onClick={() => setGiftOpen(false)}
              disabled={giftSending}
              className="absolute top-3 right-3 w-7 h-7 rounded-full text-[#5c5c5c] hover:bg-[#faf9f6] flex items-center justify-center"
              aria-label="Close"
              data-testid="gift-modal-close"
            >
              <XIcon className="w-4 h-4" />
            </button>
            <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.28em] font-bold text-[#722f37]">
              <Gift className="w-4 h-4" /> Gift a reveal
            </div>
            <h3 className="mt-2 font-serif-display text-2xl text-[#1a1a1a]" data-testid="gift-modal-title">
              Send {data?.user?.display_name || "this member"} a free reveal
            </h3>
            <p className="mt-2 text-sm text-[#5c5c5c]" data-testid="gift-modal-cost">
              {viewer?.subscription_active
                ? "Free with Prime — unlimited gifts."
                : `1 reveal credit will be deducted (${viewer?.reveal_credits || 0} available).`}
            </p>

            <label htmlFor="gift-note" className="mt-5 block text-xs font-semibold text-[#1a1a1a]">
              Add a note (optional)
            </label>
            <textarea
              id="gift-note"
              value={giftNote}
              onChange={(e) => setGiftNote(e.target.value.slice(0, 140))}
              rows={3}
              maxLength={140}
              placeholder="Say why you're sending this — a compliment or an invitation."
              className="mt-2 w-full rounded-xl border border-[#e5e0d8] bg-[#faf9f6] px-3 py-2 text-sm text-[#1a1a1a] resize-none focus:outline-none focus:border-[#722f37] focus:bg-white"
              data-testid="gift-modal-note-input"
            />
            <div className="mt-1 flex justify-end text-[11px] text-[#5c5c5c]" data-testid="gift-modal-note-count">
              {giftNote.length}/140
            </div>

            <div className="mt-4 flex items-center gap-2 justify-end">
              <button
                type="button"
                onClick={() => setGiftOpen(false)}
                disabled={giftSending}
                className="text-sm text-[#5c5c5c] px-3 py-2 hover:text-[#1a1a1a]"
                data-testid="gift-modal-cancel"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={sendGift}
                disabled={giftSending || (!viewer?.subscription_active && (viewer?.reveal_credits || 0) < 1)}
                className="btn-primary flex items-center gap-1.5 !py-2 !px-4 text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="gift-modal-send"
              >
                {giftSending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Gift className="w-4 h-4" />}
                Send gift
              </button>
            </div>
            {!viewer?.subscription_active && (viewer?.reveal_credits || 0) < 1 && (
              <p className="mt-3 text-xs text-[#a02b2b]" data-testid="gift-modal-no-credits">
                You need at least 1 reveal credit to send a gift. <Link to="/pricing" className="underline">Get more →</Link>
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
